<template>
  <v-dialog v-model="showDialog" max-width="500" persistent>
    <v-card>
      <v-card-title class="text-h6 text-center justify-center">
        Como você avalia sua experiência?
      </v-card-title>
      
      <v-card-text class="pb-0">
        <div class="rating-container">
          <div 
            v-for="rating in ratings" 
            :key="rating.value"
            class="rating-item"
            :class="{ 'selected': selectedRating === rating.value }"
            @click="selectRating(rating.value)"
          >
            <div class="emoji-wrapper">
              <span class="emoji">{{ rating.emoji }}</span>
            </div>
            <div class="rating-label">{{ rating.label }}</div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-btn text @click="closeDialog" :disabled="loading">
          Mais Tarde
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn 
          color="#F15A24" 
          dark
          @click="submitRating" 
          :disabled="!selectedRating || loading"
          :loading="loading"
        >
          Enviar Avaliação
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'CSATRating',
  props: {
    pageName: {
      type: String,
      required: true,
      default: 'Esta página'
    },
    perguntaId: {
      type: [Number, String],
      required: true
    },
    delaySeconds: {
      type: Number,
      default: 5
    },
    autoShow: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showDialog: false,
      selectedRating: null,
      loading: false,
      ratings: [
        { value: 1, emoji: '😞', label: 'Muito insatisfeito' },
        { value: 2, emoji: '😕', label: 'Insatisfeito' },
        { value: 3, emoji: '😐', label: 'Indiferente' },
        { value: 4, emoji: '🙂', label: 'Satisfeito' },
        { value: 5, emoji: '😄', label: 'Muito satisfeito' }
      ],
      timeoutId: null
    }
  },
  async mounted() {
    if (this.autoShow) {
      await this.verificarCondicoesExibicao()
    }
  },
  beforeDestroy() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }
  },
  methods: {
    async verificarCondicoesExibicao() {
      try {
        // Verificar se o usuário está logado
        if (!this.$store.state.user || !this.$store.state.user.id) {
          window.console.log('CSATRating: Usuário não logado, não exibindo dialog')
          return
        }

        // Buscar informações do usuário para verificar contador de logins e se já coletou feedback
        const { data: userData } = await this.$http.get(
          `users/${this.$store.state.user.id}?fields=id,contador_logins,feedback_coletado`
        )
        
        const contadorLogins = userData.data.contador_logins || 0
        const feedbackColetado = userData.data.feedback_coletado || false

        window.console.log(`CSATRating: Contador de logins: ${contadorLogins}, Feedback coletado: ${feedbackColetado}`)

        // Verificar condições:
        // 1. Usuário deve ter feito pelo menos 3 logins
        // 2. Feedback ainda não deve ter sido coletado
        if (contadorLogins >= 3 && !feedbackColetado) {
          // Verifica se o usuário já avaliou esta página nesta sessão
          const sessionKey = `csat_rated_${this.pageName}`
          if (sessionStorage.getItem(sessionKey)) {
            return
          }

          this.timeoutId = setTimeout(() => {
            this.showDialog = true
          }, this.delaySeconds * 1000)
        } else {
          window.console.log('CSATRating: Condições não atendidas para exibir dialog')
        }
      } catch (error) {
        window.console.error('Erro ao verificar condições de exibição do CSAT:', error)
      }
    },
    scheduleShow() {
      // Verifica se o usuário já avaliou esta página nesta sessão
      const sessionKey = `csat_rated_${this.pageName}`
      if (sessionStorage.getItem(sessionKey)) {
        return
      }

      this.timeoutId = setTimeout(() => {
        this.showDialog = true
      }, this.delaySeconds * 1000)
    },
    selectRating(value) {
      this.selectedRating = value
    },
    async submitRating() {
      if (!this.selectedRating) return

      this.loading = true

      try {
        // Determina o tipo baseado na avaliação
        let tipo = ''
        if (this.selectedRating <= 2) {
          tipo = 'Negativo'
        } else if (this.selectedRating === 3) {
          tipo = 'Neutro'
        } else {
          tipo = 'Positivo'
        }

        // Monta o payload para salvar na tabela respostas_CSAT
        // User Created e Data Created são preenchidos automaticamente pelo Directus
        const payload = {
          pergunta_id: parseInt(this.perguntaId),
          avaliacao: parseInt(this.selectedRating),
          tipo: tipo,
          status: 'published'
        }

        // Salva a resposta no banco de dados
        const response = await this.$http.post('items/respostas_CSAT', payload)
        const novaRespostaId = response.data.data.id
        
        window.console.log('✅ Resposta CSAT salva com sucesso:', response.data.data)

        // Vincula a resposta à pergunta no campo Respostas de perguntas_CSAT
        await this.vincularRespostaNaPergunta(novaRespostaId)

        // Marcar feedback como coletado no banco de dados (apenas uma vez por usuário)
        try {
          if (this.$store.state.user && this.$store.state.user.id) {
            await this.$http.patch(`users/${this.$store.state.user.id}`, {
              feedback_coletado: true
            })
            window.console.log('✅ Feedback marcado como coletado')
            
            // Atualizar store local
            if (this.$store.state.user) {
              this.$store.state.user.feedback_coletado = true
            }
          }
        } catch (err) {
          window.console.error('⚠️ Erro ao marcar feedback como coletado:', err)
          // Não bloqueia o fluxo se houver erro
        }

        // Marca como avaliado nesta sessão
        const sessionKey = `csat_rated_${this.pageName}`
        sessionStorage.setItem(sessionKey, 'true')

        // Emite evento de sucesso
        this.$emit('rating-submitted', {
          rating: this.selectedRating,
          page: this.pageName,
          perguntaId: this.perguntaId
        })

        this.closeDialog()
      } catch (error) {
        window.console.error('Erro ao enviar avaliação:', error)
        
        let errorMessage = 'Erro ao enviar avaliação. Tente novamente.'
        
        if (error.response && error.response.data) {
          if (error.response.data.errors && error.response.data.errors[0] && error.response.data.errors[0].message) {
            errorMessage = error.response.data.errors[0].message
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message
          }
        }
        
        alert(errorMessage)
      } finally {
        this.loading = false
      }
    },
    async vincularRespostaNaPergunta(respostaId) {
      try {
        // Busca a pergunta atual para obter as respostas existentes
        const perguntaResponse = await this.$http.get(
          `items/perguntas_CSAT/${this.perguntaId}?fields=respostas`
        )
        
        // Pega as respostas existentes (pode ser null, array vazio, ou array com IDs)
        let respostasExistentes = []
        if (perguntaResponse.data && perguntaResponse.data.data && perguntaResponse.data.data.respostas) {
          respostasExistentes = perguntaResponse.data.data.respostas
          // Se for array de objetos, extrai só os IDs
          if (Array.isArray(respostasExistentes) && respostasExistentes.length > 0) {
            if (typeof respostasExistentes[0] === 'object') {
              respostasExistentes = respostasExistentes.map(r => r.id || r)
            }
          }
        }
        
        // Adiciona a nova resposta ao array
        const respostasAtualizadas = [...respostasExistentes, respostaId]
        
        // Atualiza o campo respostas na tabela perguntas_CSAT
        await this.$http.patch(`items/perguntas_CSAT/${this.perguntaId}`, {
          respostas: respostasAtualizadas
        })
        
        window.console.log('✅ Resposta vinculada à pergunta com sucesso')
      } catch (error) {
        window.console.error('⚠️ Erro ao vincular resposta na pergunta:', error)
        // Não bloqueia o fluxo, pois a resposta já foi salva
      }
    },
    closeDialog() {
      this.showDialog = false
      this.selectedRating = null
      this.$emit('dialog-closed')
    },
    // Método público para mostrar o dialog manualmente
    show() {
      const sessionKey = `csat_rated_${this.pageName}`
      if (!sessionStorage.getItem(sessionKey)) {
        this.showDialog = true
      }
    }
  }
}
</script>

<style scoped>
.rating-container {
  display: flex;
  justify-content: space-around;
  align-items: flex-start;
  padding: 20px 0;
  gap: 8px;
}

.rating-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 12px 8px;
  border-radius: 12px;
  flex: 1;
  max-width: 90px;
}

.rating-item:hover {
  transform: translateY(-5px);
}

.rating-item.selected {
  background-color: transparent;
  border: none;
}

.emoji-wrapper {
  margin-bottom: 12px;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.emoji {
  font-size: 40px;
  display: block;
  transition: transform 0.2s ease;
  filter: none !important;
  opacity: 1 !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.rating-item:hover .emoji {
  transform: scale(1.2);
}

.rating-item.selected .emoji {
  transform: scale(1.15);
}

.rating-label {
  font-size: 11px;
  text-align: center;
  color: #666;
  margin-bottom: 4px;
  line-height: 1.2;
  min-height: 30px;
  display: flex;
  align-items: center;
}

.rating-item.selected .rating-label {
  color: #F15A24;
  font-weight: 500;
}

@media (max-width: 600px) {
  .rating-container {
    gap: 4px;
    padding: 15px 0;
  }

  .rating-item {
    padding: 8px 4px;
    max-width: 70px;
  }

  .emoji {
    font-size: 32px;
  }

  .rating-label {
    font-size: 9px;
    min-height: 26px;
  }
}
</style>
